import { questionService } from '../services';

// Zwei entkoppelte Ebenen (siehe productowner/audicio-change-audit-freeze.md):
//  - Ebene 1 (calcPosture/getRisk): Audit-Score/Bereiche/Befunde aus den
//    Fragebogen-Antworten. Wird bei Audit-Abschluss einmalig fixiert
//    (Assessment.frozenPosture) und danach nie mehr neu berechnet.
//  - Ebene 2 (calcProgress/UMSETZUNG_META): Umsetzungsfortschritt der
//    Kontrollen, nur nach Abschluss editierbar, ohne jede Rückwirkung auf Ebene 1.
//
// Muss mit backend/src/utils/posture.js synchron bleiben (abgesichert durch
// backend/test/postureParity.test.js).

// ── Risk helpers (Ebene 1) ──────────────────────────────────────────────
export const getRisk = (score) => {
  if (score >= 80) return { label: 'Gut',        tone: 'secure' };
  if (score >= 60) return { label: 'Mäßig',     tone: 'warning' };
  return               { label: 'Gefährdet', tone: 'critical' };
};

const STATUS_META = {
  erfuellt:        { score: 100, finding: false, excluded: false },
  teilweise:       { score: 50,  finding: true,  excluded: false },
  nicht_erfuellt:  { score: 0,   finding: true,  excluded: false },
  nicht_anwendbar: { score: null, finding: false, excluded: true },
};

// Nur zur Anzeige des ursprünglichen Befunds in der Kontrollen-Liste (Kontext,
// "warum steht das hier") — rein informativ, fließt in nichts weiter ein.
export const ORIGINAL_META = {
  erfuellt:        { label: 'Ursprünglich erfüllt',      tone: 'secure' },
  teilweise:       { label: 'Ursprünglich teilweise',    tone: 'warning' },
  nicht_erfuellt:  { label: 'Ursprünglich nicht erfüllt', tone: 'critical' },
  nicht_anwendbar: { label: 'Nicht anwendbar',           tone: 'neutral' },
};

// Nur aus der rohen Fragebogen-Antwort abgeleitet — bewusst unabhängig vom
// editierbaren umsetzungsStatus (Ebene 2 darf Ebene 1 nie beeinflussen).
export const deriveStatus = (question, answerRecord) => {
  const ans = answerRecord?.answer;
  if (ans === undefined || ans === null || ans === '') return null;
  if (question.type === 'yes_no') return ans === 'yes' ? 'erfuellt' : 'nicht_erfuellt';
  if (question.type === 'rating') {
    const r = parseInt(ans) || 0;
    if (r >= 4) return 'erfuellt';
    if (r === 3) return 'teilweise';
    return 'nicht_erfuellt';
  }
  return 'teilweise';
};

// ── Relevanz (DIN SPEC 27076, 6.4.1) ────────────────────────────────────
// Statusabfragen verzweigen den Katalog. Für jede Frage ergibt sich daraus:
//  - 'active'   — relevant, wird normal bewertet
//  - 'inactive' — irrelevant (Statusabfrage "Nein"/unbeantwortet): wird laut
//                 6.4.1 komplett aus der Wertung herausgerechnet ("dynamischer
//                 Maximalwert") — kein Score-Nenner, kein Finding, keine Kontrolle
//  - 'autofail' — gilt als NICHT ERFÜLLT: Statusabfrage mit noMeansFail (z. B.
//                 11-0 "Führen Sie Datensicherungen durch?") wurde verneint —
//                 der abhängige Block ist damit verfehlt, nicht irrelevant
export const resolveApplicability = (questions, answersMap) => {
  const byDinNr = new Map(questions.filter(q => q.dinNr).map(q => [q.dinNr, q]));
  const result = {};
  const resolve = (q) => {
    if (result[q.id] !== undefined) return result[q.id];
    let state = 'active';
    if (q.dependsOn) {
      const parent = byDinNr.get(q.dependsOn);
      if (parent) {
        const parentState = resolve(parent);
        if (parentState !== 'active') {
          // verschachtelte Zweige (z. B. 25-4 → 25-3 → 25-0) erben den Zustand
          state = parentState;
        } else {
          const ans = answersMap[parent.id]?.answer;
          if (ans === 'yes') state = 'active';
          else if (ans === 'no') state = parent.noMeansFail ? 'autofail' : 'inactive';
          else state = 'inactive'; // Statusabfrage unbeantwortet → Zweig nicht relevant
        }
      }
    }
    result[q.id] = state;
    return state;
  };
  questions.forEach(resolve);
  return result;
};

// Einzige Definition, was als (offene) Feststellung zählt (AUD-34) — muss mit
// backend/src/utils/posture.js::isFinding synchron bleiben.
//  - Statusabfragen (isStatusQuery) sind Verzweigungsfragen, keine Anforderungen → nie Finding.
//  - Irrelevante Fragen (inactive) sind aus der Wertung herausgerechnet → nie Finding.
//  - Autofail-Fragen (Block per noMeansFail-Statusabfrage verneint) → immer Finding.
//  - Sonst: unbeantwortet ist fehlende Information, kein Finding (deriveStatus === null);
//    Finding genau bei Status 'teilweise' oder 'nicht_erfuellt'.
export const isFinding = (question, answerRecord, applicability = 'active') => {
  if (question?.isStatusQuery) return false;
  if (applicability === 'inactive') return false;
  if (applicability === 'autofail') return true;
  const status = deriveStatus(question, answerRecord);
  return status === 'teilweise' || status === 'nicht_erfuellt';
};

// ── Priorität ──────────────────────────────────────────────────────────
// Aus Question.isTop/weight abgeleitet (kein eigenes Datenbank-Feld).
export const getPriority = (question) => {
  if (question.isTop) return { label: 'Hoch', tone: 'critical', value: 'hoch' };
  if ((question.weight ?? 0) >= 2) return { label: 'Mittel', tone: 'warning', value: 'mittel' };
  return { label: 'Niedrig', tone: 'neutral', value: 'niedrig' };
};

// ── Score calculation (Ebene 1) ─────────────────────────────────────────
export const calcPosture = (questions, answersMap) => {
  const applicability = resolveApplicability(questions, answersMap);
  const cats = {};
  let answeredCount = 0;
  let notApplicableCount = 0;
  let totalControls = 0;

  questions.forEach(q => {
    // Statusabfragen sind Verzweigungsfragen ohne Punktwert (Tabelle A.1) —
    // sie fließen weder in Score noch in Zähler ein.
    if (q.isStatusQuery) return;

    if (applicability[q.id] === 'inactive') {
      // 6.4.1: irrelevante Anforderungen werden aus dem Maximalwert herausgerechnet.
      notApplicableCount += 1;
      return;
    }

    if (!cats[q.category]) cats[q.category] = { sum: 0, scored: 0, total: 0, findings: 0 };
    const bucket = cats[q.category];
    bucket.total += 1;
    totalControls += 1;

    if (applicability[q.id] === 'autofail') {
      // Block per Statusabfrage als nicht erfüllt bewertet (sum += 0) —
      // die Bewertung ist durch die Statusabfrage getroffen, zählt als beantwortet.
      answeredCount += 1;
      bucket.scored += 1;
      bucket.findings += 1;
      return;
    }

    const status = deriveStatus(q, answersMap[q.id]);

    // Unbeantwortet: senkt den Score (Nenner), ist aber KEIN Finding (fehlende
    // Daten). Findings werden ausschließlich über isFinding gezählt (AUD-34).
    if (status === null) {
      bucket.scored += 1;
      return;
    }

    answeredCount += 1;
    const meta = STATUS_META[status];
    if (meta.excluded) return;

    bucket.sum += meta.score;
    bucket.scored += 1;
    if (isFinding(q, answersMap[q.id])) bucket.findings += 1;
  });

  const categories = Object.entries(cats).map(([name, d]) => ({
    name,
    score:    d.scored > 0 ? Math.round(d.sum / d.scored) : 0,
    findings: d.findings,
    total:    d.total,
  }));

  const overall = categories.length
    ? Math.round(categories.reduce((s, c) => s + c.score, 0) / categories.length)
    : 0;

  return {
    overallScore:   overall,
    categories,
    totalFindings:  categories.reduce((s, c) => s + c.findings, 0),
    // Dynamischer Maximalwert (6.4.1): nur relevante Anforderungen zählen.
    totalControls,
    answeredCount,
    notApplicableCount,
  };
};

// Abgeschlossene Audits sind unveränderliche Momentaufnahmen: der beim Abschluss
// fixierte Snapshot wird angezeigt, nie mehr aus den Antworten neu berechnet.
export const fetchPosture = async (assessment) => {
  if (assessment.status === 'completed' && assessment.frozenPosture) {
    return assessment.frozenPosture;
  }
  const [qRes, aRes] = await Promise.all([
    questionService.getAllQuestions(assessment.templateId),
    questionService.getAnswersByAssessment(assessment.id),
  ]);
  const map = {};
  aRes.data.forEach(a => { map[a.questionId] = a; });
  return calcPosture(qRes.data, map);
};

// ── Umsetzungsfortschritt (Ebene 2, Maßnahmenliste) ─────────────────────
export const UMSETZUNG_STATUSES = ['offen', 'in_umsetzung', 'erfuellt', 'nicht_anwendbar'];

export const UMSETZUNG_META = {
  offen:           { label: 'Offen',           tone: 'critical' },
  in_umsetzung:    { label: 'In Umsetzung',    tone: 'warning' },
  erfuellt:        { label: 'Erfüllt',         tone: 'secure' },
  nicht_anwendbar: { label: 'Nicht anwendbar', tone: 'neutral' },
};

export const getUmsetzungMeta = (status) => UMSETZUNG_META[status || 'offen'];

// Effektiver Umsetzungsstatus einer Kontrolle: ein explizit gesetzter Status
// hat immer Vorrang. Ohne expliziten Status braucht eine Kontrolle, die schon
// ursprünglich erfüllt war, keine Maßnahme — sie startet nicht bei "Offen",
// sondern spiegelt den bereits erreichten Ist-Zustand.
export const getEffectiveProgress = (question, answerRecord) => {
  if (answerRecord?.umsetzungsStatus) return answerRecord.umsetzungsStatus;
  return deriveStatus(question, answerRecord) === 'erfuellt' ? 'erfuellt' : 'offen';
};

// Die eine Definition, was eine "Kontrolle" ist (Ebene 2): alle relevanten
// Anforderungen des Audits. Statusabfragen (Verzweigungsfragen) und irrelevante
// Zweige sind keine Kontrollen — Autofail-Anforderungen dagegen schon (dort
// besteht ja gerade Handlungsbedarf).
export const selectControls = (questions, answersMap) => {
  const applicability = resolveApplicability(questions, answersMap);
  return questions.filter(q => !q.isStatusQuery && applicability[q.id] !== 'inactive');
};

// "Nicht anwendbar" zählt als erledigt/ausgenommen, fehlender Status als "offen".
export const calcProgress = (questions, answersMap) => {
  const controls = selectControls(questions, answersMap);
  let done = 0;
  controls.forEach(q => {
    const status = getEffectiveProgress(q, answersMap[q.id]);
    if (status === 'erfuellt' || status === 'nicht_anwendbar') done += 1;
  });
  const total = controls.length;
  return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
};
